import React, { useEffect, useMemo, useState } from 'react'
import { useLang } from '../lib/useLang'
import ColumnEditor from './ColumnEditor'
import ModButton from '../ui/ModButton/ModButton'

export default function HumanReviewCard({ request, onSubmit, busy, initialColumnsSchema, initialTarget, initialTables }) {
  const { t } = useLang()
  const agent = request?.agent || 'agent'
  const req = request?.request || {}
  const fields = Array.isArray(req.fields_needed) ? req.fields_needed : []
  const [message, setMessage] = useState('')
  const [err, setErr] = useState('')
  const [columnsRows, setColumnsRows] = useState([])
  const [target, setTarget] = useState(initialTarget || '')
  const [activeTableIndex, setActiveTableIndex] = useState(0)

  const hasMultipleTables = Array.isArray(initialTables) && initialTables.length > 1

  useEffect(() => {
    // Initialize columnsRows from the active table or legacy single schema
    try {
      const rows = []
      let cs = initialColumnsSchema || {}
      if (hasMultipleTables && initialTables[activeTableIndex]?.columns) {
        cs = initialTables[activeTableIndex].columns
      }
      for (const [name, meta] of Object.entries(cs)) {
        rows.push({ name, type: String(meta?.type || ''), description: String(meta?.description || '') })
      }
      setColumnsRows(rows)
    } catch {}
  }, [initialColumnsSchema, activeTableIndex, hasMultipleTables, initialTables])

  const columns_schema_patch = useMemo(() => {
    // compute diff vs initialColumnsSchema so we don't resend unchanged
    const diff = {}
    try {
      const initial = initialColumnsSchema || {}
      const current = {}
      for (const r of columnsRows || []) {
        const n = (r.name || '').trim()
        if (!n) continue
        const t = (r.type || '').trim()
        const d = (r.description || '').trim()
        const entry = {}
        if (t) entry.type = t
        if (d) entry.description = d
        current[n] = entry
      }
      const names = new Set([...Object.keys(initial || {}), ...Object.keys(current)])
      for (const name of names) {
        const a = initial?.[name] || {}
        const b = current?.[name]
        if (!b) continue // removed columns не отправляем
        const at = (a?.type || '').trim()
        const bt = (b?.type || '').trim()
        const ad = (a?.description || '').trim()
        const bd = (b?.description || '').trim()
        if (at !== bt || ad !== bd || !(name in initial)) {
          diff[name] = b
        }
      }
    } catch {}
    return diff
  }, [columnsRows, initialColumnsSchema])

  const canSubmit = !busy

  const title = (() => {
    let m = ''
    try {
      m = String(req?.message || '').trim()
    } catch {}
    return m ? t('humanReview.titleWithMessage', { agent, message: m }) : t('humanReview.title', { agent })
  })()

  return (
    <div className='flex flex-col rounded-xl border border-ink/10 bg-ink/[0.03]' style={{ maxWidth: '900px', maxHeight: 'calc(100vh - 250px)' }}>
      {/* Header */}
      <div className='flex-shrink-0 border-b border-ink/10 px-6 py-4'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-500/10'>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='text-amber-600'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='8' x2='12' y2='12' />
              <line x1='12' y1='16' x2='12.01' y2='16' />
            </svg>
          </div>
          <div className='flex-1'>
            <div className='text-base font-semibold text-ink'>{title}</div>
            {fields.length > 0 && (
              <div className='mt-1 text-xs text-ink'>{t('humanReview.fieldsNeeded', { fields: fields.join(', ') })}</div>
            )}
          </div>
        </div>
      </div>

      {/* Content - scrollable */}
      <div className='flex-1 space-y-4 overflow-y-auto px-6 py-4' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.09) transparent', minHeight: 0 }}>
        {/* Comment */}
        <div>
          <label className='mb-2 block text-sm font-medium text-ink'>
            {t('humanReview.comment')} <span className='text-ink'>{t('humanReview.optional')}</span>
          </label>
          <textarea
            className='w-full resize-none rounded-lg border border-ink/10 bg-white px-3 py-2 text-sm text-ink placeholder-ink/30 transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20'
            rows={2}
            placeholder={t('humanReview.addNote')}
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
        </div>

        {/* Target column */}
        <div>
          <label className='mb-2 block text-sm font-medium text-ink'>{t('humanReview.targetColumn')}</label>
          <select
            className='w-full rounded-lg border border-ink/10 bg-white px-3 py-2 text-sm text-ink transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20'
            value={target}
            onChange={e => setTarget(e.target.value)}
          >
            <option value=''>{t('humanReview.selectTarget')}</option>
            {Array.isArray(columnsRows) &&
              columnsRows.map((r, i) => (
                <option key={`${r.name}-${i}`} value={(r.name || '').trim()}>
                  {(r.name || '').trim()}
                </option>
              ))}
          </select>
        </div>

        {/* Table selector (multi-table only) */}
        {hasMultipleTables && (
          <div className='flex gap-2'>
            {initialTables.map((tbl, idx) => {
              const name = (tbl.path || `Table ${idx + 1}`).split('/').pop()
              return (
                <button
                  key={idx}
                  onClick={() => setActiveTableIndex(idx)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                    idx === activeTableIndex
                      ? 'bg-accent/15 text-accent border border-accent'
                      : 'bg-ink/[0.03] text-ink border border-ink/10 hover:text-ink'
                  }`}
                >
                  {name}
                </button>
              )
            })}
          </div>
        )}

        {/* Column Schema */}
        <div>
          <label className='mb-2 block text-sm font-medium text-ink'>
            {t('humanReview.columnsLabel')}
            {hasMultipleTables && (
              <span className='ml-2 text-ink'>
                ({(initialTables[activeTableIndex]?.path || '').split('/').pop()})
              </span>
            )}
          </label>
          <ColumnEditor value={columnsRows} onChange={setColumnsRows} />
        </div>

        {/* Error message */}
        {err && (
          <div className='rounded-lg border border-red-500/30 bg-red-500/10 p-3'>
            <div className='flex items-start gap-2'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='mt-0.5 flex-shrink-0 text-red-600'>
                <circle cx='12' cy='12' r='10' />
                <line x1='12' y1='8' x2='12' y2='12' />
                <line x1='12' y1='16' x2='12.01' y2='16' />
              </svg>
              <div className='text-xs text-red-500'>{err}</div>
            </div>
          </div>
        )}

        {/* Submit button */}
        <div className='mt-6 flex justify-end pb-2'>
          <ModButton
            severity='primary'
            onClick={() => {
              setErr('')
              const rp = {}
              if (target && !rp.target) rp.target = target
              onSubmit &&
                onSubmit({
                  requirements_patch: Object.keys(rp).length ? rp : undefined,
                  message: message || undefined,
                  columns_schema_patch: Object.keys(columns_schema_patch || {}).length
                    ? columns_schema_patch
                    : undefined,
                })
            }}
            disabled={!canSubmit}
          >
            {busy ? t('humanReview.submitting') : t('humanReview.confirmContinue')}
          </ModButton>
        </div>
      </div>
    </div>
  )
}
